// Pure helpers for the mortgage-loan-program-type-missing decide flow
// (Grant's ruling, 2026-07-12). The old recommendation sent the operator
// to "edit the loan via the service-record detail page" — a page with no
// program-type editor, the exact dead-end shape Nate hit on client-since.
// The rail now renders the FULL program picker — Conventional / FHA / VA /
// USDA / Jumbo / HELOC / Reverse / Non-QM / Other ("sometimes we get
// HELOC or Reverse or Non QM or Other") — and "verify" carries
// params.loan_program_type (+ an optional short note, mainly for Other);
// the backend writes it onto the loan and resolves the issue in one click.
//
// Root-safe (imports only the pure shared options module) so it's
// unit-tested from tests/web/ — same convention as client-since-issue.ts.

import {
  TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS,
  isTraditionalLoanProgramType,
} from "../../../shared/types/mortgage";

export { TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS };

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface ProgramTypeIssueLike {
  detectorSlug: string;
}

// Issues whose "verify" carries a loan program type to the backend (and
// whose rail block renders the program picker).
export function isProgramTypeIssue(issue: ProgramTypeIssueLike): boolean {
  return issue.detectorSlug === "mortgage-loan-program-type-missing";
}

// Client-side floor matching the backend's validation. The backend
// remains the source of truth; this only enables/disables Apply.
export function isValidProgramTypeInput(s: string): boolean {
  return isTraditionalLoanProgramType(s.trim());
}

export function programTypeLabel(value: string): string {
  return (
    TRADITIONAL_LOAN_PROGRAM_TYPE_OPTIONS.find((o) => o.value === value)
      ?.label ?? value
  );
}

// Plain-language before→after for the program-carrying verify (UX rule
// #3) — replaces the generic verify copy when this slug is active.
export function programTypeConsequence(
  issue: ProgramTypeIssueLike,
  value: string,
  note = "",
): string | null {
  if (!isProgramTypeIssue(issue)) return null;
  const trimmed = value.trim();
  if (isValidProgramTypeInput(trimmed)) {
    const label = programTypeLabel(trimmed);
    const noteBit = note.trim() ? ` with the note “${note.trim()}”` : "";
    return `Sets this loan's program type to ${label}${noteBit} and resolves this issue. Because a person confirmed it, it won't be re-flagged — even “Other” sticks. Audited; reversible for 60 seconds.`;
  }
  return "Pick the loan program first — setting it is the whole action here. Use the lender, purpose, and amount shown in the evidence; “Other” is a real answer (add a short note saying what it is).";
}
